import { useState } from "react";
import { Outlet, NavLink, isRouteErrorResponse, useRouteError } from "react-router";
import "./index.css";

import BurgerIcon from "./icons/BurgerIcon";
import MoonIcon from "./icons/MoonIcon";

const pages: Record<string, string> = {
  'Home': "/",
  'Travel': "/travel",
  'Things to Do': "/thingstodo",
  //'Dress Code': "/dresscode",
  'Photos': "/photos",
};

function getDark() {
  return (
    localStorage.theme === "dark" ||
    (!("theme" in localStorage) &&
      window.matchMedia("(prefers-color-scheme: dark)").matches)
  );
}

export function HydrateFallback() {
  return <div className="bg-white dark:bg-bg w-dvw h-dvh"></div>;
}

function Shell({ children }: { children: React.ReactNode }) {
  const [menu, setMenu] = useState(false);
  const [dark, setDark] = useState(getDark());

  function toggleTheme() {
    const cur_dark = getDark();
    localStorage.theme = cur_dark ? "light" : "dark";
    setDark(!cur_dark);
  }

  document.documentElement.classList.toggle(
    "dark",
    localStorage.theme === "dark" ||
      (!("theme" in localStorage) &&
        window.matchMedia("(prefers-color-scheme: dark)").matches),
  );

  return (
    <>
      <div className="absolute bottom-6 right-6 z-50">
        <button
          className="h-10 w-10 p-2 bg-pink-400 rounded-full hover:bg-pink-400/50 transition-all duration-150"
          onClick={toggleTheme}
        >
          <MoonIcon
            className="h-full w-full cursor-pointer transition duration-150 ease-in-out"
            fill={dark ? "white" : "black"}
          />
        </button>
      </div>
      <div className="text-bg bg-white dark:text-white dark:bg-bg font-fancy w-screen h-screen overflow-y-scroll scrollbar-thumb-pink-400 scrollbar-thin">
        <div className="w-full h-full relative z-10 flex flex-col">
          {/* mobile */}
          <header className="flex shrink-0 md:hidden h-16 mb-2 bg-light dark:bg-dark drop-shadow-md">
            <div className="max-w-7xl w-full h-full flex flex-row justify-center items-center p-4 gap-4">
              <NavLink to={{ pathname: "/" }}>
                <h1 className="text-2xl font-script font-bold cursor-pointer md:text-2xl hover:text-gray-500 transition duration-150 ease-in-out">
                  Katelynn & Thomas
                </h1>
              </NavLink>
              <div className="justify-end ml-auto" />
              <div className="justify-end h-full">
                <BurgerIcon
                  className="stroke-black dark:stroke-white h-full cursor-pointer"
                  onClick={() => setMenu(!menu)}
                />
              </div>
            </div>
          </header>

          {/* desktop */}
          <div className="flex flex-col justify-center">
            <NavLink to={{ pathname: "/" }} className="flex flex-col items-center">
              <img src="/public/flowers-from-site.webp?quality=80&size=1920" className="w-full object-scale-down" width={1920} height={1080} fetchPriority="high" />
              <h1 className="text-6xl text-center font-bold font-script my-4">Katelynn & Thomas</h1>
            </NavLink>
            <h4 className="text-2xl text-center">March 6, 2027 · Anchorage, AK</h4>
            <div className="hidden md:flex justify-center gap-6 py-8">
              {Object.keys(pages).map((page) => (
                <div className="justify-end" key={page}>
                  <NavLink
                    prefetch="intent"
                    className={({ isActive }) =>
                      `text-xl cursor-pointer hover:text-gray-500 transition-all ease-in-out ${
                        isActive ? "underline underline-offset-4 decoration-pink-400" : ""
                      }`
                    }
                    to={{ pathname: pages[page] }}
                  >
                    {page}
                  </NavLink>
                </div>
              ))}
            </div>
          </div>

          <main className="grow page">{children}</main>
        </div>

        {/* mobile menu */}
        <div
          className={`fixed w-full top-16 left-0 right-0 bottom-0 z-50 bg-light dark:bg-dark transition-all duration-300 ease-in-out ${
            menu ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <div className="flex flex-col items-end p-4 gap-4">
            {Object.keys(pages).map((page) => (
              <div key={page} className="p-2">
                <NavLink
                  prefetch="viewport"
                  className="text-2xl"
                  to={{ pathname: pages[page] }}
                  onClick={() => setMenu(false)}
                >
                  {page}
                </NavLink>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export function LayoutErrorBoundary() {
  const error = useRouteError();
  if (!isRouteErrorResponse(error)) return <></>;

  return (
    <Shell>
      <div className="p-4 h-full w-full flex flex-row justify-center items-center max-w-7xl mx-auto">
        <h1 className="text-center text-8xl">{error.status} {error.statusText}</h1>
      </div>
    </Shell>
  );
}

export function Layout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

export default Layout;
